import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { isEqual } from 'lodash';
import { GridColDef, GridPinnedColumnFields } from '@mui/x-data-grid-pro';
import { SORT_ORDER } from '@common/types';

export interface IPatientsTableFilters {
  search: string;
  sort_by: string;
  sort_order: SORT_ORDER;
  page: number;
  limit: number;
  account_status?: string;
  mbi?: string;
  phone_number?: string;
  program_name?: string;
  program_status?: string;
  program_tier?: string;
  birthdate?: string;
  recently_viewed?: boolean;
  wellness_coordinator_ids?: string[];
  care_manager_ids?: string[];
  care_navigator_ids?: string[];
  practice_ids?: string[];
  provider_ids?: string[];
  partner_id?: string;
  building_ids?: string[];
  appliedFilterCount?: number;
}

export const emptyPatientsFilters: IPatientsTableFilters = {
  search: '',
  sort_order: 'asc',
  sort_by: 'name',
  page: 1,
  limit: 100,
  account_status: '',
  mbi: '',
  phone_number: '',
  program_name: '',
  program_status: '',
  program_tier: '',
  birthdate: '',
  wellness_coordinator_ids: [],
  care_manager_ids: [],
  care_navigator_ids: [],
  practice_ids: [],
  provider_ids: [],
  partner_id: '',
  building_ids: [],
  appliedFilterCount: 0,
};

type TKeyOfPatientsFilters = keyof IPatientsTableFilters;

export const getAppliedPatientsFiltersCount = (
  appliedFilters: IPatientsTableFilters,
  initialFiltersList: IPatientsTableFilters,
): number => {
  const filtersKeyList = Object.keys(appliedFilters).filter(
    key =>
      key !== 'page' &&
      key !== 'limit' &&
      key !== 'appliedFilterCount' &&
      key !== 'search' &&
      key !== 'sort_order' &&
      key !== 'sort_by' &&
      key !== 'partner_id',
  ) as TKeyOfPatientsFilters[];

  return filtersKeyList.reduce(
    (count, key) =>
      isEqual(initialFiltersList[key], appliedFilters[key]) ? count : count + 1,
    0,
  );
};

export type PersistedPatientsColumn = Pick<
  GridColDef,
  | 'field'
  | 'headerName'
  | 'width'
  | 'minWidth'
  | 'flex'
  | 'sortable'
  | 'filterable'
  | 'disableColumnMenu'
  | 'align'
  | 'headerAlign'
>;

export const initialPatientsColumns: PersistedPatientsColumn[] = [
  {
    field: 'id',
    headerName: 'ID',
    width: 150,
    minWidth: 150,
    sortable: true,
    filterable: false,
  },
  {
    field: 'name',
    headerName: 'Name',
    minWidth: 180,
    flex: 1,
    sortable: true,
    filterable: false,
  },
  {
    field: 'birthdate',
    headerName: 'Date of birth',
    width: 130,
    minWidth: 130,
    sortable: true,
    filterable: false,
  },
  {
    field: 'minutes_this_month',
    headerName: 'CM Minutes',
    width: 140,
    minWidth: 140,
    sortable: true,
    filterable: false,
    align: 'center',
    headerAlign: 'center',
  },
  {
    field: 'mbi',
    headerName: 'Primary insurance',
    minWidth: 160,
    flex: 1,
    sortable: false,
    filterable: false,
  },
  {
    field: 'account_status',
    headerName: 'Account status',
    width: 140,
    minWidth: 140,
    sortable: false,
    filterable: false,
    align: 'center',
    headerAlign: 'center',
  },
  {
    field: 'guide_status',
    headerName: 'GUIDE status',
    minWidth: 160,
    flex: 1,
    sortable: false,
    filterable: true,
  },
  {
    field: 'programs',
    headerName: 'Service Lines & Programs',
    minWidth: 160,
    flex: 1,
    sortable: false,
    filterable: false,
  },
  {
    field: 'open_task_count',
    headerName: 'Open tasks',
    width: 110,
    minWidth: 110,
    sortable: true,
    filterable: false,
  },
  {
    field: 'provider_name',
    headerName: 'PCP',
    width: 160,
    minWidth: 160,
    sortable: true,
    filterable: false,
  },
  {
    field: 'practice_name',
    headerName: 'Practice',
    minWidth: 160,
    flex: 1,
    sortable: true,
    filterable: false,
  },
  {
    field: 'care_navigator_name',
    headerName: 'Care navigator',
    width: 170,
    minWidth: 170,
    sortable: true,
    filterable: false,
  },
  {
    field: 'building_name',
    headerName: 'Location',
    width: 180,
    minWidth: 180,
    sortable: true,
    filterable: false,
  },
];

export const initialPinnedColumns: GridPinnedColumnFields = {
  left: [],
  right: [],
};

interface IPatientsStore {
  columns: PersistedPatientsColumn[];
  setColumns: (columns: PersistedPatientsColumn[]) => void;
  pinnedColumns: GridPinnedColumnFields;
  setPinnedColumns: (pinnedColumns: GridPinnedColumnFields) => void;
  resetView: () => void;

  filters: IPatientsTableFilters;
  setFilters: (newFilters: Partial<IPatientsTableFilters>) => void;
  resetFilters: () => void;
  additionalFilters: string;
  setAdditionalFilters: (additionalFilters: string) => void;
}

export const usePatientsStore = create<IPatientsStore>()(
  persist(
    set => ({
      columns: initialPatientsColumns,
      setColumns: columns => set({ columns }),
      pinnedColumns: initialPinnedColumns,
      setPinnedColumns: pinnedColumns => set({ pinnedColumns }),
      resetView: () =>
        set({
          columns: initialPatientsColumns,
          pinnedColumns: initialPinnedColumns,
        }),

      filters: emptyPatientsFilters,
      setFilters: newFilters =>
        set(state => {
          const updatedFilters = { ...state.filters, ...newFilters };
          return {
            filters: {
              ...updatedFilters,
              appliedFilterCount: getAppliedPatientsFiltersCount(
                updatedFilters,
                emptyPatientsFilters,
              ),
            },
          };
        }),
      resetFilters: () =>
        set(state => ({
          filters: {
            ...emptyPatientsFilters,
            partner_id: state.filters.partner_id,
          },
          additionalFilters: state.filters.partner_id
            ? JSON.stringify({ partner_id: state.filters.partner_id })
            : '',
        })),
      additionalFilters: '',
      setAdditionalFilters: additionalFilters => set({ additionalFilters }),
    }),
    {
      name: 'patients-store',
      partialize: state => ({
        columns: state.columns,
        pinnedColumns: state.pinnedColumns,
      }),
    },
  ),
);
